import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';

import {
  OrdersService,
  QueryOrderListFilterRequest,
  QueryOrderListItemModel,
  Pageable
} from '../../../core/services/order/order.service';

import { PaginationState } from '../../../core/models/common/pageable.model';

@Component({
  selector: 'app-orders-list',
  imports: [
    CommonModule,
    FormsModule,
    AlertComponent,
    LoadingComponent,
    ButtonComponent,
    PaginationComponent
  ],
  templateUrl: './orders-list.component.html',
  styleUrl: './orders-list.component.scss'
})
export class OrdersListComponent implements OnInit, OnDestroy {
  searchOrderId = '';
  searchCpf = '';
  searchClientK = ''; // não implementado

  hasGanError = false;

  currentOrderPreset = '';

  qFromUrl = '';

  error: string | null = null;
  notImplementedMessage: string | null = null;

  loading = false;
  data: QueryOrderListItemModel[] = [];

  paginationState: PaginationState = {
    currentPage: 0,
    itemsPerPage: 10,
    loading: false,
    totalItems: 0,
    currentPageItems: 0
  };

  startIndex = 0;
  endIndex = 0;

  private qpSub?: Subscription;

  constructor(
    private readonly orders: OrdersService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.qpSub = this.route.queryParams.subscribe((params) => {
      const page = parseInt(params['page'] ?? '0', 10);
      const pageSize = parseInt(params['pageSize'] ?? '10', 10);

      this.currentOrderPreset = (params['order'] ?? '').toString().toLowerCase();

      this.paginationState.currentPage = Number.isNaN(page) ? 0 : page;
      this.paginationState.itemsPerPage = Number.isNaN(pageSize) ? 10 : pageSize;

      this.qFromUrl = (params['q'] ?? '').toString();

      this.applyQToInputs(this.qFromUrl);

      this.fetchList(this.paginationState.currentPage, this.paginationState.itemsPerPage);
    });
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
  }

  onPageChange = (page: number, pageSize: number): void => {
    this.navigateWithQuery({ page, pageSize });
  };

  search(): void {
    this.notImplementedMessage = null;

    if ((this.searchClientK || '').trim()) {
      this.notImplementedMessage =
        'Busca por "K do cliente" ainda não foi implementada. Use ID do pedido e/ou CPF.';
    }

    const q = this.buildQ().trim() || null;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q,
        page: 0,
        pageSize: this.paginationState.itemsPerPage,
        refresh: Date.now()
      }
    });
  }

  clear(): void {
    this.searchOrderId = '';
    this.searchCpf = '';
    this.searchClientK = '';
    this.notImplementedMessage = null;
    this.qFromUrl = '';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        order: null,
        q: null,
        page: 0,
        pageSize: 10
      },
      queryParamsHandling: 'merge'
    });
  }

  private fetchList(page: number, pageSize: number): void {
    this.paginationState.loading = true;
    this.loading = true;
    this.error = null;

    const q = (this.qFromUrl || '').trim() || this.buildQ();
    const preset = (this.currentOrderPreset ?? '').toLowerCase();

    const req: QueryOrderListFilterRequest = {
      page: page + 1,
      pageSize: pageSize,
      q: q || undefined,

      incompletedOrders: preset === 'incompleted' ? true : undefined,
      waitingOrders: preset === 'waiting' ? true : undefined,
      invoicedOrders: preset === 'invoiced' ? true : undefined
    } as any;

    this.orders.list(req).subscribe({
      next: (res: Pageable<QueryOrderListItemModel>) => {
        const items = (res as any).content ?? [];
        const info = (res as any).page ?? (res as any).paging ?? null;

        this.data = items;

        if (info) {
          const currentPage1 = Number(info.page ?? info.currentPage ?? (page + 1)) || (page + 1);
          const total = Number(info.total ?? 0) || 0;

          this.paginationState.currentPage = Math.max(0, currentPage1 - 1);
          this.paginationState.itemsPerPage = pageSize;
          this.paginationState.totalItems = total;
          this.paginationState.currentPageItems = items.length;
        } else {
          this.paginationState.currentPage = page;
          this.paginationState.itemsPerPage = pageSize;
          this.paginationState.totalItems = items.length;
          this.paginationState.currentPageItems = items.length;
        }

        for (const ord of this.data as any[]) {
          const ganMessage = ord.details?.extraData?.pendingMessage?.actionTimelineDescription
          if(ganMessage?.includes?.("gan solicitou aprovação de pagamento do pedido")) {
            ord.details.extraData.pendingMessage.actionTimelineDescription = "gan está aguardando aprovação do pagamento"
          }
          this.hasGanError = ord.details?.extraData?.pendingMessage?.actionTimelineDescription?.includes("gan solicitou faturamento do pedido") && ord.details?.extraData?.pendingMessage?.statusDescription?.includes("Pedido com inconsistência")

          const isInvoicedWithoutDetails = !ord.details && ord.status === 'invoiced';

          if (ord?.details?.extraData?.pendingMessage?.action !== 'GAN_DISPATCH_INVOICE' && !this.hasGanError && !isInvoicedWithoutDetails) continue;

          if(this.hasGanError){
            ord.details.color = '#EF4444' ;
          }

          if (!ord.details) {
            ord.details = { status: ord.status };
          }

          const orderId = ord?.orderId ?? ord?.details?.orderId;
          if (!orderId) continue;

          ord.ganLoading = true;

          this.orders.gan(orderId).subscribe({
            next: (ganInfo) => {
              ord.details = ord.details ?? {};
              ord.details.gan = ganInfo;
              if (isInvoicedWithoutDetails) {
                ord.details.color = '#EF4444';
              }
              ord.ganLoading = false;
            },
            error: () => {
              ord.details = ord.details ?? {};
              ord.details.gan = { error: true };
              ord.ganLoading = false;
            }
          });
        }

        this.computeFooter();
        this.paginationState.loading = false;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);

        this.data = [];
        this.paginationState.totalItems = 0;
        this.paginationState.currentPageItems = 0;

        this.computeFooter();
        this.error = 'Falha ao carregar pedidos.';
        this.paginationState.loading = false;
        this.loading = false;
      }
    });
  }

  setPreset(order: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { order: order || null, page: 0 },
      queryParamsHandling: 'merge'
    });
  }

  openOrder(orderId?: string): void {
    if (!orderId) return;
    // this.router.navigate(['/orders/order', orderId]);
    const url = this.router.serializeUrl(this.router.createUrlTree(['/orders/order', orderId]));
    window.open(url,'_blank');
  }

  openOrderVtex(orderId?: string): void {
    if (!orderId) return;
    this.router.navigate(['/orders/vtex', orderId]);
  }

  private buildQ(): string {
    const orderId = (this.searchOrderId || '').trim();
    const cpf = this.normalizeCpf(this.searchCpf);

    const tokens: string[] = [];
    if (orderId) tokens.push(`${this.sanitizeFullText(orderId)}`);
    if (cpf) tokens.push(`${cpf}`);

    if (!tokens.length) return '';
    return tokens.join(' ');
  }

  private applyQToInputs(q: string): void {
    const clean = (q || '').trim();
    if (!clean) return;

    const tokens = clean.split(/\s+/g).filter(Boolean);

    const cpfToken = tokens.find(t => /^\d{11}$/.test(t));
    if (cpfToken) this.searchCpf = cpfToken;

    const others = tokens.filter(t => t !== cpfToken);
    if (others.length) this.searchOrderId = others.join(',');
  }

  private normalizeOrderId(value: string): string {
    return value
      .replace(/[\r\n]+/g, ',')
      .replace(/\s*,\s*/g, ',')
      .replace(/\s+/g, ',')
      .replace(/,+/g, ',')
      .replace(/^,|,$/g, '');
  }

  private sanitizeFullText(value: string): string {
    return value.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  onOrderIdChange(value: string): void {
    this.searchOrderId = this.normalizeOrderId(value);
  }

  onCpfChange(value: string): void {
    this.searchCpf = this.normalizeCpf(value);
  }

  private normalizeCpf(value: any): string {
    if (value == null) return '';
    const digits = String(value).replace(/\D/g, '');
    return digits.slice(0, 11);
  }

  private navigateWithQuery(pagination: { page: number; pageSize: number }, clearAll?: boolean): void {
    const baseParams: any = {
      page: pagination.page,
      pageSize: pagination.pageSize
    };

    const q = this.buildQ().trim();

    const filterParams: any = clearAll
      ? { q: null, order: null }
      : {
          q: q ? q : null,
          order: (this.currentOrderPreset || '').trim() ? this.currentOrderPreset : null
        };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...filterParams, ...baseParams },
      queryParamsHandling: 'merge'
    });
  }

  private computeFooter(): void {
    const total = this.paginationState.totalItems ?? 0;
    const size = this.paginationState.itemsPerPage ?? 10;
    const page0 = this.paginationState.currentPage ?? 0;

    if (!this.data.length) {
      this.startIndex = 0;
      this.endIndex = 0;
      return;
    }

    const start = page0 * size + 1;
    const end = Math.min(page0 * size + this.data.length, total || start - 1 + this.data.length);

    this.startIndex = start;
    this.endIndex = end;
  }

  asDateLabel(value: any): string {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('pt-BR');
  }

  getDetailsLabel(o: any): string {
    if ((this.currentOrderPreset || '').toLowerCase() === 'incompleted') {
      const actionMsg = o?.details?.extraData?.pendingMessage?.actionToResolveMessage;
      const statusDesc = o?.details?.extraData?.pendingMessage?.statusDescription;

      if (actionMsg && statusDesc) {
        return `${actionMsg} - ${statusDesc}`;
      }

      return actionMsg || statusDesc || '-';
    }

    const details = o?.details;
    if (!details) return 'Processando status';

    if (details.status === 'invoiced') {
      if (details.gan?.tipoNf === 'MF' || details.gan?.tipoNf === 'VM') {
        return 'Pedido faturado no GAN';
      }
      return 'Pedido Faturado';
    }

    const pm = details?.extraData?.pendingMessage;

    if (pm?.actionTimelineDescription?.includes('conectala solicitou aprov')) {
      return 'Parceiro não envio faturamento a Conectá, assim Conectá não fez o envio do FMSO2 ao GAN';
    }

    if (pm?.action === 'KRUZER_DISPATCH_DFDL_BILLING') {
      return `${pm?.statusDescription ?? '-'} | Parceiro não enviou pagamento a VTEX`;
    }

    if (pm?.action === 'CONECTALA_DISPATCH_PAYMENT_APPROVED') {
      return `${pm?.actionTimelineDescription ?? '-'} | Parceiro não enviou faturamento a VTEX`;
    }

    const timeline = pm?.actionTimelineDescription ?? '';
    if (timeline.includes('CPF/EMAIL')) {
      return `${timeline || '-'} | Necessário contactar o SAC para ajustar`;
    }

    if(o?.details?.gan?.tipoNf === 'MF' || o?.details?.gan?.tipoNf === 'VM'){
      return 'Pedido faturado no GAN'
    }

    if ((pm?.actionTimelineDescription ?? '').includes('kruzer enviou dfdl order')) {
      return `kruzer não enviou dfdl order e billing`;
    }

    if(this?.hasGanError){
      return `Inconsistência no GAN`
    }

    if ((pm?.actionTimelineDescription ?? '').includes('kruzer enviou contrato')) {
      return `kruzer não enviou contrato prime ao GAN`;
    }

    if ((pm?.actionTimelineDescription ?? '').includes('gan solicitou aprova')) {
      return `GAN aguardando aprovação de pagamento do pedido`;
    }

    if ((pm?.actionTimelineDescription ?? '').includes('gan solicitou aprova')) {
      return `GAN aguardando aprovação de pagamento do pedido`;
    }

    return pm?.actionTimelineDescription ?? 'Processando status';
  }

  showGanExtra(o: any): boolean {
    if (o?.details?.status === 'invoiced') {
      return o?.details?.gan?.tipoNf === 'MF' || !!o?.details?.gan?.inconPagto;
    }

    return (
      o?.details?.gan?.tipoNf === 'MF' ||
      !!o?.details?.gan?.inconPagto ||
      o?.details?.status === 'payment-pending'
    );
  }
}
